import { useEffect } from "react";
import { ArrowRight, CalendarClock, Compass, Headphones, GraduationCap, ListChecks, Target, Trophy, Video, UserRound, Users } from "lucide-react";
import { Reveal, Logo, trackEvent } from "@/components/site/Reveal";

const TEST_URL = "/test";
const BOUSSOLE_URL = "https://boussole.yassineabou.fr";

const SIGNS = [
  "Il remet tout au dernier moment, puis se plaint d'être débordé.",
  "Il travaille longtemps, mais retient peu.",
  "Après une mauvaise note, il se décourage ou lâche la matière.",
  "Il ne voit pas à quoi servent ses efforts.",
];

const STEPS = [
  { icon: Target, title: "1. Un objectif qui a du sens", desc: "Votre enfant précise ce qu'il vise et pourquoi. Un objectif qu'on comprend, on le tient plus longtemps." },
  { icon: ListChecks, title: "2. Des exercices concrets", desc: "Organisation, concentration, gestion du jour de contrôle : chaque thème se travaille par de courts exercices, à son rythme." },
  { icon: Video, title: "3. Un suivi humain", desc: "Selon la formule, un point en visio tous les 15 jours pour ajuster, encourager et garder le cap." },
];

const PROFILS = [
  {
    id: "etudiant", icon: GraduationCap, name: "Collégien, lycéen, étudiant", tag: "Espace Étudiant",
    objectifs: ["Poursuite d'études et orientation", "Recherche de stage ou d'alternance", "Organisation personnelle", "Trouver du sens à ses études"],
    methode: "Des petits pas concrets qui rapprochent de l'objectif : routines de travail, préparation des contrôles et des oraux.",
    resultats: "Plus de méthode, de confiance et de sérénité.",
  },
  {
    id: "performer", icon: Trophy, name: "Sportif, compétiteur", tag: "Espace Performer",
    objectifs: ["Trouver sa singularité et son cap sportif", "Une posture et une routine de performance", "Repérer ses pensées parasites", "Viser une progression en technique, en tactique et au classement"],
    methode: "Des outils clairs de préparation mentale, à travailler entre les entraînements et avant les échéances.",
    resultats: "Plus de régularité, de confiance et de maîtrise le jour J.",
  },
  {
    id: "adulte", icon: Compass, name: "Jeune adulte en quête de cap", tag: "Espace Jeune adulte",
    objectifs: ["Trouver une orientation, un métier ou une formation qui plaît", "Donner du sens à sa démarche et à sa future fonction", "Être aligné avec ses valeurs et ses objectifs", "Retrouver un équilibre au quotidien"],
    methode: "Un état des lieux, puis un projet construit pas à pas, à son rythme.",
    resultats: "Plus de clarté et de cohérence dans ses choix.",
  },
];

const LEARNS = [
  "Mesurer l'influence de son entourage et choisir ses appuis",
  "Travailler régulièrement plutôt que par à-coups",
  "Se fixer des objectifs précis, qui ont du sens pour lui",
  "Installer des routines de travail et de récupération",
  "Repérer ses pensées parasites et les remplacer par des pensées utiles",
  "Identifier les habitudes qui freinent son développement, pour s'en détacher en conscience",
  "Prendre conscience de ses capacités réelles",
];

const Parents = () => {
  useEffect(() => {
    document.title = "Coach scolaire en ligne pour collégiens et lycéens | Yassine Abou";
    window.scrollTo(0, 0);
  }, []);

  const goTest = () => trackEvent("parents_clic_test", { page: "parents" });
  const goApp = () => trackEvent("parents_clic_boussole", { page: "parents" });

  return (
    <div className="min-h-screen bg-obsidian text-chalk" data-testid="parents-page">
      <header className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3" aria-label="Retour à l'accueil"><Logo size={36} /><span className="font-heading font-black uppercase tracking-wide text-lg">Yassine Abou</span></a>
        <a href={TEST_URL} onClick={goTest} className="hidden sm:inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-semibold hover:bg-white/10">Faire le test gratuit</a>
      </header>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 pt-10 pb-16 sm:pt-20">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-gold">Collégiens, lycéens, sportifs, jeunes adultes — et leurs parents</p>
          <h1 className="mt-4 font-heading font-black uppercase leading-[1.02] text-5xl sm:text-6xl lg:text-7xl" data-testid="parents-title">
            Avoir du talent, c'est bien.<br /><span className="text-ember">Savoir l'exploiter, c'est mieux.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
            Boussole est une application de coaching en ligne, conçue par un professeur et ancien compétiteur. Elle aide les collégiens, lycéens, sportifs et jeunes adultes à trouver une méthode et un cap : s'organiser, tenir dans la durée et aborder les échéances plus sereinement.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={TEST_URL} onClick={goTest} data-testid="parents-cta-test" className="inline-flex items-center gap-2 rounded-full bg-ember px-7 py-3.5 font-semibold text-white hover:bg-emberdark transition-colors">
              Faire le test gratuit (2 minutes) <ArrowRight className="h-4 w-4" />
            </a>
            <a href={BOUSSOLE_URL} onClick={goApp} className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 font-semibold hover:bg-white/10">Découvrir l'application</a>
          </div>
          <p className="mt-3 text-sm text-slate-400">Le test est gratuit et ne demande aucune donnée avant le résultat.</p>
        </Reveal>
      </section>

      <section className="bg-coal border-y border-white/5 py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <Reveal>
            <h2 className="font-heading font-black uppercase text-3xl sm:text-4xl">Vous reconnaissez peut-être…</h2>
            <ul className="mt-6 space-y-3">
              {SIGNS.map((s) => (<li key={s} className="flex gap-3 text-slate-200 text-lg"><span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-ember" />{s}</li>))}
            </ul>
            <p className="mt-6 text-slate-300 max-w-2xl">Ce n'est pas un manque d'intelligence. C'est souvent un manque de méthode et de repères, deux choses qui s'apprennent. À cet âge, un jeune est aussi fortement influencé par son environnement (entourage, habitudes, écrans, pensées qu'il se répète), le plus souvent sans s'en rendre compte.</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <Reveal>
          <h2 className="font-heading font-black uppercase text-3xl sm:text-4xl">Comment ça se passe</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-3">
            {STEPS.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="rounded-2xl border border-white/10 bg-slate2 p-6">
                <Icon className="h-6 w-6 text-gold" />
                <p className="mt-4 font-heading font-bold uppercase text-xl">{title}</p>
                <p className="mt-2 text-slate-300 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <Reveal>
          <h2 className="font-heading font-black uppercase text-3xl sm:text-4xl">Ce que votre enfant apprend avec Boussole</h2>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {LEARNS.map((l) => (<li key={l} className="flex gap-3 rounded-xl border border-white/10 bg-slate2 p-4 text-slate-200"><span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-gold" />{l}</li>))}
          </ul>
          <div className="mt-10 rounded-2xl border border-gold/30 bg-slate2 p-6 sm:p-8" data-testid="parents-tiers">
            <Users className="h-7 w-7 text-gold" />
            <h3 className="mt-3 font-heading font-bold uppercase text-2xl">Le rôle d'un tiers de confiance</h3>
            <p className="mt-3 text-slate-300 leading-relaxed">
              À l'adolescence, les échanges avec un parent sur le travail et l'avenir deviennent vite tendus : un même conseil, venant d'un parent, est facilement vécu comme une pression. Un accompagnant extérieur, qui n'est ni le parent ni l'enseignant de l'enfant, peut dire les mêmes choses sans enjeu affectif. Il désamorce la tension et permet au jeune d'accepter le message plutôt que de s'y opposer par réflexe.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-10" data-testid="parents-visuel">
        <Reveal>
          <img src="/deux-univers.jpg" width="1000" height="1000" loading="lazy" alt="Yassine Abou, professeur et coach, entre un ring de sport de combat et une salle de classe : Boussole, deux univers, un même cap." className="mx-auto w-full max-w-md rounded-2xl border border-white/10 shadow-xl" />
        </Reveal>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-12" data-testid="parents-presentation-audio">
        <Reveal>
          <div className="rounded-2xl border border-gold/30 bg-slate2 p-5 sm:p-6">
            <div className="flex items-center gap-3">
              <Headphones className="h-6 w-6 text-gold" />
              <div>
                <h2 className="font-heading font-bold uppercase text-lg">La méthode Boussole en 1 min 44</h2>
                <p className="text-sm text-slate-300">Yassine Abou, professeur et coach, présente les trois espaces et la façon de travailler.</p>
              </div>
            </div>
            <audio controls preload="none" className="mt-4 w-full" onPlay={() => trackEvent("parents_ecoute_presentation")}>
              <source src="/presentation-boussole.m4a" type="audio/mp4" />
              Votre navigateur ne peut pas lire cet audio.
            </audio>
          </div>
        </Reveal>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24" data-testid="parents-profils">
        <Reveal>
          <h2 className="font-heading font-black uppercase text-3xl sm:text-4xl">Trois profils, trois parcours</h2>
          <p className="mt-4 text-slate-300 leading-relaxed max-w-3xl">
            Les ateliers de Boussole sont construits pour trois publics. À sa première connexion, chacun choisit son espace : les exemples, les objectifs et le rythme s'adaptent à sa situation. <b className="text-chalk">Repérez celui qui correspond à votre enfant</b> et faites le test gratuit de ce profil.
          </p>
          <div className="mt-8 grid gap-4 lg:grid-cols-3">
            {PROFILS.map((pr) => (
              <div key={pr.id} className="flex flex-col rounded-2xl border border-white/10 bg-slate2 p-6" data-testid={`profil-${pr.id}`}>
                <pr.icon className="h-7 w-7 text-gold" />
                <h3 className="mt-3 font-heading font-bold uppercase text-xl">{pr.name}</h3>
                <p className="text-xs uppercase tracking-widest text-slate-400">{pr.tag}</p>
                <p className="mt-4 text-sm font-semibold text-chalk">Objectifs</p>
                <ul className="mt-1 space-y-1 text-sm text-slate-300">{pr.objectifs.map((o) => <li key={o} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />{o}</li>)}</ul>
                <p className="mt-4 text-sm font-semibold text-chalk">Façon de travailler</p>
                <p className="mt-1 text-sm text-slate-300">{pr.methode}</p>
                <p className="mt-4 text-sm font-semibold text-chalk">Résultats visés</p>
                <p className="mt-1 text-sm text-slate-300">{pr.resultats}</p>
                <a href={`/test?profil=${pr.id}`} onClick={() => trackEvent("parents_clic_test_profil", { profil: pr.id })}
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-ember px-5 py-3 text-sm font-semibold text-white hover:bg-emberdark transition-colors">
                  Faire le test gratuit <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            ))}
          </div>
          <div className="mt-6 rounded-2xl border border-gold/30 bg-slate2 p-5 text-slate-200" data-testid="parents-communaute">
            <b className="text-gold">Pour tous : une communauté bienveillante.</b> Quel que soit le parcours, chacun trouve un lien, un lieu et des interlocuteurs pour partager, lors du coaching en visio tous les 15 jours (selon la formule).
          </div>
          <p className="mt-4 text-sm text-slate-400">Pour un collégien ou un lycéen, c'est l'espace Étudiant. Les objectifs décrits sont des pistes de travail : les résultats dépendent de l'investissement de chacun.</p>
        </Reveal>
      </section>

      <section className="bg-coal border-y border-white/5 py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 grid gap-8 sm:grid-cols-[auto,1fr] items-start">
          <Reveal className="contents">
            <UserRound className="h-10 w-10 text-gold" />
            <div>
              <h2 className="font-heading font-black uppercase text-3xl sm:text-4xl">Qui est derrière Boussole ?</h2>
              <p className="mt-4 text-slate-300 leading-relaxed max-w-2xl">
                Yassine Abou est ingénieur de formation (bac +5), ceinture noire 4ᵉ dan, professeur en lycée professionnel, éducateur sportif titulaire d'un diplôme jeunesse et sports, et arbitre national. Avant l'enseignement, il a mené une carrière dans le privé, au sein d'un grand groupe international, et a pris son indépendance dès 21 ans en quittant le domicile familial. Ancien compétiteur en sports de combat, il a longtemps vécu la pression des échéances. Il a construit Boussole pour transmettre ce qui l'a aidé à tenir : un objectif clair, des routines simples, et le courage de recommencer après un échec.
              </p>
              <p className="mt-3 text-sm text-slate-400 max-w-2xl">Boussole est un accompagnement éducatif et personnel. Elle ne remplace ni un médecin ni un professionnel de santé.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-16 sm:py-24 text-center">
        <Reveal>
          <Compass className="mx-auto h-8 w-8 text-gold" />
          <h2 className="mt-4 font-heading font-black uppercase text-3xl sm:text-5xl">Commencez par faire le point</h2>
          <p className="mt-4 text-slate-300 max-w-xl mx-auto">En 2 minutes, le test donne une première recommandation pour votre enfant. Ensuite, vous décidez.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href={TEST_URL} onClick={goTest} data-testid="parents-cta-test-bas" className="inline-flex items-center gap-2 rounded-full bg-ember px-7 py-3.5 font-semibold text-white hover:bg-emberdark transition-colors">Faire le test gratuit <ArrowRight className="h-4 w-4" /></a>
            <a href="/#tarifs" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 font-semibold hover:bg-white/10"><CalendarClock className="h-4 w-4" /> Voir les formules et tarifs</a>
          </div>
        </Reveal>
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-sm text-slate-500">
        <a href="/cgv" className="underline underline-offset-2 hover:text-slate-300">Conditions générales de vente</a> · © Yassine Abou
      </footer>
    </div>
  );
};

export default Parents;
